import { supabase } from '@/integrations/supabase/client';

type ErrorSource = 'frontend' | 'backend' | 'supabase';

interface ErrorLogData {
  error_type: string;
  message: string;
  stack_trace?: string;
  metadata?: Record<string, any>;
  source: ErrorSource;
  user_id?: string;
}

/**
 * Logs an error to the Supabase errors table
 * @param error The error object or message
 * @param source The source of the error (frontend, backend, supabase)
 * @param metadata Any additional context about the error
 * @param userId Optional user ID if available
 */
export async function logError(
  error: Error | unknown, 
  source: ErrorSource, 
  metadata?: Record<string, any>,
  userId?: string
) {
  try {
    const errorObj = error instanceof Error ? error : new Error(String(error));
    
    const errorData: ErrorLogData = {
      error_type: errorObj.name || 'UnknownError',
      message: errorObj.message || 'Unknown error occurred',
      stack_trace: errorObj.stack,
      metadata,
      source
    };
    
    // Only add userId if it's provided
    if (userId) {
      errorData.user_id = userId;
    }
    
    const { error: insertError } = await supabase
      .from('errors')
      .insert(errorData);
      
    if (insertError) {
      // If we can't log to Supabase, fall back to console
      console.error('Failed to log error to database:', insertError);
      console.error('Original error:', error);
    }
  } catch (loggingError) {
    // Last resort fallback
    console.error('Error in error logging system:', loggingError);
    console.error('Original error:', error);
  }
} 